import { Input, ISelectOption, Panel, RangeSlider, Select, ValidationState } from '@salecycle/ui-component-library';
import { TemplatePosition, TemplatePositionDescriptions } from 'entities/campaign/rulesets/template-position';
import { Variant } from 'entities/rulesetSplitTest';
import { OnsiteTypes, Template } from 'entities/template';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  validateVariantAudienceShare,
  validateVariantPosition,
  validateVariantSelector,
  validateVariantTemplate,
} from '../splitTestValidator';

export interface Props {
  index?: number;
  variant: Variant;
  isDefault?: boolean;
  templates: Template[];
  updateVariant?: (index: number, variant: Variant) => void;
}

export const VariantPanel: React.FC<Props> = props => {
  const { t } = useTranslation('campaigns');
  const [weight, setWeight] = React.useState(props.variant.weight);

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    setWeight(props.variant.weight);
  }, [props.variant.weight]);

  const templates = props.templates.map(tmpl => ({
    id: tmpl.id,
    label: tmpl.name,
  }));

  const currentTemplate =
    props.variant.templateId && props.templates.find((tmpl): boolean => tmpl.id === props.variant.templateId);
  const isTemplateInjected = currentTemplate && currentTemplate.type === OnsiteTypes.Injected;

  const positionOptions = Object.keys(TemplatePosition).map(key => ({
    label: t(TemplatePositionDescriptions[key]),
    id: TemplatePosition[key],
  }));

  const isValidAudienceShare = (variant: Variant): ValidationState =>
    validateVariantAudienceShare(variant).isValid ? 'complete' : 'warning';
  const isValidTemplate = (variant: Variant): ValidationState =>
    validateVariantTemplate(variant).isValid ? 'complete' : 'warning';
  const isValidTemplateSelector = (variant: Variant): ValidationState =>
    validateVariantSelector(variant).isValid ? 'complete' : 'warning';
  const isValidTemplatePosition = (variant: Variant): ValidationState =>
    validateVariantPosition(variant).isValid ? 'complete' : 'warning';

  const onWeightChange = (value: number): void => {
    setWeight(value);
    props.updateVariant(props.index, { weight: value });
  };

  const onTemplateIdChange = (template: ISelectOption<string>): void =>
    !props.isDefault && props.updateVariant(props.index, { templateId: template.value });

  const onSelectorChange = (templateSelector: string): void => props.updateVariant(props.index, { templateSelector });

  const onPositionChange = (templatePosition: ISelectOption<string>): void =>
    !props.isDefault &&
    props.updateVariant(props.index, { templatePosition: templatePosition.value as TemplatePosition });

  return (
    <div>
      <h4>{props.variant.name}</h4>
      <Panel>
        <RangeSlider
          label="Audience Share"
          value={weight}
          onChange={onWeightChange}
          disabled={props.isDefault}
          validationState={isValidAudienceShare(props.variant)}
          min={1}
          max={99}
        />
        <Select
          ariaLabel="Template"
          label="Template"
          inputId="TemplateSelect"
          items={templates}
          disabled={props.isDefault ? props.isDefault : false}
          value={{ value: props.variant.templateId, label: props.variant.name }}
          onChange={onTemplateIdChange}
          validationState={(!!props.variant.templateId && isValidTemplate(props.variant)) || undefined}
        />
        {isTemplateInjected && (
          <>
            <Input
              label="Selector"
              ariaLabel="Selector"
              inputId="VariantSelectorInput"
              value={props.variant.templateSelector}
              onChange={onSelectorChange}
              disabled={props.isDefault}
              validationState={
                (!!props.variant.templateSelector && isValidTemplateSelector(props.variant)) || undefined
              }
            />
            <Select
              ariaLabel="Position"
              label="Position"
              inputId="PositionSelect"
              value={{
                value: props.variant.templatePosition,
                label: t(TemplatePositionDescriptions[props.variant.templatePosition]),
              }}
              items={positionOptions}
              onChange={onPositionChange}
              validationState={
                (!!props.variant.templatePosition && isValidTemplatePosition(props.variant)) || undefined
              }
            />
          </>
        )}
      </Panel>
    </div>
  );
};

export default VariantPanel;
